import React, { useState, useEffect } from "react";
import {
  Card,
  Tabs,
  Button,
  Table,
  Tag,
  Space,
  Statistic,
  Row,
  Col,
  Modal,
  message,
  Tooltip,
  Progress,
  Typography,
  Divider,
  Badge,
  Layout,
} from "antd";
import {
  PlusOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  ReloadOutlined,
  TeamOutlined,
  CalendarOutlined,
  DollarOutlined,
  BarChartOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { usePayrollProcessing, PayrollProvider } from "./store/context";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";
import { PayrollPeriod, PayrollStatus } from "../../types/hr";
import { formatCurrency, formatDate, completedPayrollCount } from "./utils";
import PeriodFormModal from "./components/PeriodFormModal";
import PeriodDetailsModal from "./components/PeriodDetailsModal";
import EmployeeManagementModal from "./components/EmployeeManagementModal";
import VariableManagementModal from "./components/VariableManagementModal";
import CalculationLaunchModal from "./components/CalculationLaunchModal";
import ValidationModal from "./components/validation/ValidationModal";

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Content } = Layout;

// Composant pour les statistiques rapides
const QuickStats: React.FC = () => {
  const { state } = usePayrollProcessing();
  const { periods, statistics } = state;

  // CORRECTION: Identifier la période utilisée pour les statistiques (même logique que le dashboard)
  const activePeriod = 
    periods.find(p => p.status === "processing" || p.status === "calculated") ||
    periods.find(p => p.status === "variables_set") ||
    periods[0]; // La plus récente par défaut

  return (
    <>
      {/* CORRECTION: Indicateur de la période utilisée pour les statistiques */}
      {activePeriod && (
        <div style={{ marginBottom: 16, textAlign: "center" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            📊 Statistiques basées sur la période: <Text strong>{activePeriod.name}</Text>
            {statistics ? "" : " (chargement en cours...)"}
          </Text>
        </div>
      )}
      
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Périodes Actives"
              value={periods.filter((p) => p.status !== "closed").length}
              prefix={<CalendarOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Employés en Cours"
              value={statistics?.current_period_employees || activePeriod?.total_employees || 0}
              prefix={<TeamOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Calculs Terminés"
              value={completedPayrollCount(statistics || activePeriod || {})}
              suffix={`/ ${statistics?.total_calculations || activePeriod?.total_employees || 0}`}
              prefix={<CalculatorOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Masse Salariale"
              value={statistics?.total_gross_salary || activePeriod?.total_gross_salary || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined style={{ color: "#722ed1" }} />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

// Composant pour le statut des périodes
const isValidationInProgress = (period: PayrollPeriod) => {
  const totalCalculations = Number(
    period.total_calculations || period.total_employees || 0
  );
  const validatedCalculations = Number(period.validated_calculations || 0);

  return (
    period.status === "calculated" &&
    validatedCalculations > 0 &&
    validatedCalculations < totalCalculations
  );
};

const StatusTag: React.FC<{ period: PayrollPeriod }> = ({ period }) => {
  const statusConfig = {
    draft: { color: "default", text: "Brouillon" },
    variables_set: { color: "blue", text: "Pret pour calcul" },
    processing: { color: "processing", text: "En Traitement" },
    calculated: { color: "warning", text: "Calculé" },
    partial_error: { color: "error", text: "Erreurs Partielles" },
    validated: { color: "success", text: "Validé" },
    paid: { color: "success", text: "Payé" },
    closed: { color: "default", text: "Fermé" },
    archived: { color: "default", text: "Archivé" },
  };

  if (isValidationInProgress(period)) {
    return <Tag color="processing">Validation en cours</Tag>;
  }

  const config = statusConfig[period.status] || statusConfig.draft;
  return <Tag color={config.color}>{config.text}</Tag>;
};

// Composant principal de gestion des périodes
const PeriodManagement: React.FC = () => {
  const { state, deletePeriod, loadPeriods, reopenCalculations } =
    usePayrollProcessing();
  const { periods, loading } = state;

  // TODO: Migrer tous les usages de actions vers les fonctions directes
  const actions = { deletePeriod, loadPeriods, reopenCalculations };

  const [selectedPeriod, setSelectedPeriod] = useState<PayrollPeriod | null>(
    null
  );
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [showPeriodDetails, setShowPeriodDetails] = useState(false);
  const [showEmployeeManagement, setShowEmployeeManagement] = useState(false);
  const [showVariableManagement, setShowVariableManagement] = useState(false);
  const [showCalculationLaunch, setShowCalculationLaunch] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);

  const handleDeletePeriod = (period: PayrollPeriod) => {
    Modal.confirm({
      title: "Supprimer la période de paie",
      content: `Êtes-vous sûr de vouloir supprimer la période "${period.name}" ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          if (period.id) await actions.deletePeriod(period.id);
          message.success("Période supprimée avec succès");
        } catch (error) {
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };

  const handleLaunchCalculations = async (period: PayrollPeriod) => {
    setSelectedPeriod(period);
    setShowCalculationLaunch(true);
  };

  const handleReopenPeriod = (period: PayrollPeriod) => {
    Modal.confirm({
      title: "Rouvrir la periode calculee",
      icon: <ExclamationCircleOutlined />,
      content:
        'Cette action supprime les calculs existants et remet la periode en statut "Pret pour calcul".',
      okText: "Rouvrir",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          if (period.id) await actions.reopenCalculations(period.id);
        } catch (error) {
          message.error("Erreur lors de la reouverture");
        }
      },
    });
  };

  const getProgressInfo = (period: PayrollPeriod) => {
    const total = Number(period.total_employees || 0);
    const completed = completedPayrollCount(period);
    const errors = period.error_calculations || 0;

    if (total === 0) return { percent: 0, status: "normal" as const };

    const percent = Math.round((completed / total) * 100);
    let status: "success" | "active" | "exception" | "normal" = "normal";

    if (errors > 0) status = "exception";
    else if (completed === total) status = "success";
    else if (completed > 0) status = "active";

    return { percent, status };
  };

  const columns = [
    {
      title: "Période",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: PayrollPeriod) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {formatDate(record.start_date)} - {formatDate(record.end_date)}
          </Text>
        </div>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (_: PayrollStatus, record: PayrollPeriod) => (
        <StatusTag period={record} />
      ),
    },
    {
      title: "Employés",
      dataIndex: "total_employees",
      key: "employees",
      render: (total: number, record: PayrollPeriod) => (
        <Badge
          count={total}
          style={{ backgroundColor: "#52c41a" }}
          title={`${total} employé(s)`}
        />
      ),
    },
    {
      title: "Progression",
      key: "progress",
      render: (_: any, record: PayrollPeriod) => {
        const { percent, status } = getProgressInfo(record);
        return (
          <div style={{ width: "120px" }}>
            <Progress
              percent={percent}
              status={status}
              size="small"
              format={(percent) =>
                `${completedPayrollCount(record)}/${
                  record.total_employees || 0
                }`
              }
            />
          </div>
        );
      },
    },
    {
      title: "Masse Salariale",
      dataIndex: "total_gross_salary",
      key: "salary",
      render: (amount: number) => (
        <Text strong style={{ color: "#1890ff" }}>
          {formatCurrency(amount || 0)}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: PayrollPeriod) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedPeriod(record);
                setShowPeriodDetails(true);
              }}
            />
          </Tooltip>

          {record.status === "draft" && (
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedPeriod(record);
                  setShowPeriodForm(true);
                }}
              />
            </Tooltip>
          )}

          <Tooltip title="Gérer les employés">
            <Button
              type="text"
              icon={<TeamOutlined />}
              onClick={() => {
                setSelectedPeriod(record);
                setShowEmployeeManagement(true);
              }}
              disabled={["processing", "calculated", "validated", "paid", "closed", "archived"].includes(record.status)}
            />
          </Tooltip>

          <Tooltip title="Gérer les variables">
            <Button
              type="text"
              icon={<BarChartOutlined />}
              onClick={() => {
                setSelectedPeriod(record);
                setShowVariableManagement(true);
              }}
              disabled={["processing", "calculated", "validated", "paid", "closed", "archived"].includes(record.status)}
            />
          </Tooltip>

          {(record.status === "partial_error" ||
            record.status === "variables_set" ||
            (record.status === "calculated" && Number(record.validated_calculations || 0) < Number(record.total_employees || 0))) && (
            <Tooltip title="Lancer les calculs">
              <Button
                type="text"
                icon={<PlayCircleOutlined />}
                onClick={() => handleLaunchCalculations(record)}
                style={{ color: "#52c41a" }}
              />
            </Tooltip>
          )}

          {record.status === "calculated" && (
            <Tooltip title="Valider les calculs">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  setSelectedPeriod(record);
                  setShowValidationModal(true);
                }}
                style={{ color: "#1890ff" }}
              />
            </Tooltip>
          )}

          {record.status === "calculated" &&
            (record.validated_calculations || 0) === 0 && (
            <Tooltip title="Rouvrir la periode">
              <Button
                type="text"
                icon={<RollbackOutlined />}
                onClick={() => handleReopenPeriod(record)}
                style={{ color: "#fa8c16" }}
              />
            </Tooltip>
          )}

          {record.status === "validated" && (
            <Tooltip title="Consulter les bulletins">
              <Button
                type="text"
                icon={<FilePdfOutlined />}
                href="/rh/bulletins-paie"
                style={{ color: "#722ed1" }}
              />
            </Tooltip>
          )}

          {record.status === "draft" && (
            <Tooltip title="Supprimer">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeletePeriod(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title="Gestion des Périodes de Paie"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => actions.loadPeriods()}
              loading={loading.periods}
            >
              Actualiser
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedPeriod(null);
                setShowPeriodForm(true);
              }}
            >
              Nouvelle Période
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={periods}
          loading={loading.periods}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} périodes`,
          }}
        />
      </Card>

      {/* Modales */}
      <PeriodFormModal
        visible={showPeriodForm}
        period={selectedPeriod}
        onCancel={() => {
          setShowPeriodForm(false);
          setSelectedPeriod(null);
        }}
        onSuccess={() => {
          setShowPeriodForm(false);
          setSelectedPeriod(null);
          actions.loadPeriods();
        }}
      />

      <PeriodDetailsModal
        visible={showPeriodDetails}
        period={selectedPeriod}
        onCancel={() => {
          setShowPeriodDetails(false);
          setSelectedPeriod(null);
        }}
      />

      <EmployeeManagementModal
        visible={showEmployeeManagement}
        period={selectedPeriod}
        onCancel={() => {
          setShowEmployeeManagement(false);
          setSelectedPeriod(null);
        }}
        onSuccess={() => {
          actions.loadPeriods();
        }}
      />

      <VariableManagementModal
        visible={showVariableManagement}
        period={selectedPeriod}
        onCancel={() => {
          setShowVariableManagement(false);
          setSelectedPeriod(null);
        }}
        onSuccess={() => {
          actions.loadPeriods();
        }}
      />

      <CalculationLaunchModal
        visible={showCalculationLaunch}
        period={selectedPeriod}
        onCancel={() => {
          setShowCalculationLaunch(false);
          setSelectedPeriod(null);
        }}
        onSuccess={() => {
          setShowCalculationLaunch(false);
          setSelectedPeriod(null);
          actions.loadPeriods();
        }}
      />

      <ValidationModal
        visible={showValidationModal}
        period={selectedPeriod}
        onCancel={() => {
          setShowValidationModal(false);
          setSelectedPeriod(null);
        }}
        onSuccess={() => {
          actions.loadPeriods();
        }}
      />
    </>
  );
};

// Dashboard avec aperçu général
const PayrollDashboard: React.FC = () => {
  const { state, loadPeriods, loadStatistics } = usePayrollProcessing();
  const { selectedCompany } = useSelection();

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods, selectedCompany]);

  // CORRECTION: Charger automatiquement les statistiques de la période la plus récente
  useEffect(() => {
    if (state.periods.length > 0 && !state.loading.periods) {
      // Trouver la période la plus appropriée (en cours de traitement, calculée, ou la plus récente)
      const activePeriod = 
        state.periods.find(p => p.status === "processing" || p.status === "calculated") ||
        state.periods.find(p => p.status === "variables_set") ||
        state.periods[0]; // La plus récente par défaut
      
      if (activePeriod?.id) {
        console.log(`🔄 Chargement automatique des statistiques pour la période: ${activePeriod.name}`);
        loadStatistics(activePeriod.id).catch(error => {
          console.warn('Erreur lors du chargement des statistiques:', error);
          // Ne pas afficher d'erreur à l'utilisateur, les valeurs resteront à 0
        });
      }
    }
  }, [state.periods, state.loading.periods, loadStatistics]);

  return (
    <div style={{ padding: "0 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <CalculatorOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          Traitement de la Paie
        </Title>
        <Text type="secondary">
          Gérez les périodes de paie, calculez les salaires et générez les
          bulletins
        </Text>
      </div>

      <QuickStats />

      <Divider />

      <PeriodManagement />
    </div>
  );
};

// Composant principal avec Provider
const PayrollProcessing: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user } = useAuth();
  return (
    <PayrollProvider key={`${user?.id}:${selectedCompany}`}>
      <Content style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
        <PayrollDashboard />
      </Content>
    </PayrollProvider>
  );
};

export default PayrollProcessing;
